/**
 * Manager Registration Magic Link Verification Page
 * Handles token verification and account creation
 */

import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  managerVerifyRegistrationMagicLink,
  managerSession,
  ApiClientError,
} from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';
import { useRunOnce } from '../../hooks/useRunOnce';
import { createLogger } from '@resida/shared';
import { Button } from '../../components/ui/button';

const logger = createLogger('landlord:pages:auth:MagicLinkRegisterVerifyPage');

type PageState = 'loading' | 'success' | 'error';

export function MagicLinkRegisterVerifyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setSession } = useAuth();

  const [state, setState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const verifyMagicLink = useCallback(async () => {
    const token = searchParams.get('token');

    if (!token) {
      logger.error('No token provided in URL');
      setState('error');
      setErrorMessage('Invalid magic link. No verification token found.');
      return;
    }

    try {
      logger.info('Verifying registration magic link');

      // Verify token and create account
      await managerVerifyRegistrationMagicLink(token);

      // Fetch session
      const session = await managerSession();
      if (session) {
        setSession(session);
      }

      setState('success');

      // Redirect to dashboard after short delay
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1500);
    } catch (err) {
      logger.error('Magic link verification failed:', err);

      if (err instanceof ApiClientError) {
        if (err.code === 'INVALID_CREDENTIALS') {
          setErrorMessage('This magic link is invalid or has expired. Please request a new one.');
        } else if (err.code === 'REGISTRATION_FAILED') {
          setErrorMessage('An account with this email already exists. Please sign in instead.');
        } else {
          setErrorMessage(err.message || 'Verification failed. Please try again.');
        }
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
      }

      setState('error');
    }
  }, [navigate, searchParams, setSession]);

  // Single-use token: a re-run is rejected with 401 and would send the new user to login.
  useRunOnce(verifyMagicLink);

  return (
    <div className="min-h-screen bg-muted/50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-xl shadow-sm border border-border p-6 md:p-8 text-center">
          {state === 'loading' && (
            <>
              <div className="w-10 h-10 mx-auto mb-6 border-4 border-muted border-t-primary rounded-full animate-spin" />
              <h2 className="text-xl font-semibold text-foreground mb-3">
                Creating Your Account...
              </h2>
              <p className="text-sm text-muted-foreground">
                Please wait while we verify your email address.
              </p>
            </>
          )}

          {state === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-success text-success-foreground text-4xl font-bold flex items-center justify-center">
                ✓
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-3">
                Account Created!
              </h2>
              <p className="text-sm text-muted-foreground">
                Redirecting to your dashboard...
              </p>
            </>
          )}

          {state === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-destructive text-destructive-foreground text-4xl font-bold flex items-center justify-center">
                ✕
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-3">
                Verification Failed
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                {errorMessage}
              </p>
              <Button
                onClick={() => navigate('/register')}
                className="min-h-[44px]"
              >
                Back to Registration
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MagicLinkRegisterVerifyPage;
